import { ActionMenu, IconButton } from '@kanos/ui';
import { formatDate } from '@kanos/web-core';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { notificationTitle } from '@kanos/domain';
import { Bell, CircleHelp, Inbox } from 'lucide-react';
import { openTasksQuery, TASK_POLL_MS } from './firm/queries';
import { useMe } from './me';
import {
  notificationsQuery,
  UNREAD_POLL_MS,
  useReadNotifications,
} from './firm/notification-queries';
import { messageTarget } from './firm/message-target';

/** How many open tasks the bell lists; the start page holds them all. */
const SHOWN = 8;

/** Where a task is dealt with: a question with the obligations, an inquiry with the inquiries. */
function placeOf(task: { kind: string; key: string }): '/obligations' | '/inquiries' | '/' {
  if (task.kind === 'clarification') return '/obligations';
  return task.key.startsWith('inquiry_received/') ? '/inquiries' : '/';
}

/**
 * What the bell shows, kept current - once for the app: the bell stands in the bar and in the
 * phone's bar, the start page lists the tasks too; each asking on its own timer asked three times.
 */
export function FirmPolling() {
  const me = useMe();
  useQuery({ ...openTasksQuery(me.activeTenantId), refetchInterval: TASK_POLL_MS });
  useQuery({ ...notificationsQuery(me.activeTenantId, true), refetchInterval: UNREAD_POLL_MS });
  return null;
}

/**
 * The bell at the right of the bar (after Corly): what waits for the firm - questions to
 * clarify and notices - with their number on it, current every few seconds. Each entry leads
 * to where it is dealt with.
 */
export function TaskBell() {
  const me = useMe();
  const navigate = useNavigate();
  const tasks = useQuery(openTasksQuery(me.activeTenantId));
  const inbox = useQuery(notificationsQuery(me.activeTenantId, true));
  const read = useReadNotifications(me.activeTenantId);
  const open = tasks.data?.tasks ?? [];
  const mine = inbox.data?.items ?? [];
  const unread = inbox.data?.unread ?? 0;
  const count = open.length + unread;
  const label = [
    unread > 0 && `Ungelesene Mitteilungen: ${String(unread)}`,
    open.length === 0
      ? 'Offene Aufgaben: keine'
      : `Offene Aufgaben: ${tasks.data?.more ? 'mehr als ' : ''}${String(open.length)}`,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <ActionMenu
      label="Offene Aufgaben"
      trigger={
        <IconButton aria-label={label} className="relative">
          <Bell aria-hidden className="size-5" />
          {count > 0 && (
            <span
              aria-hidden
              className="absolute -top-0.5 -right-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-accent-text px-1 text-[0.6875rem] font-bold text-ink-contrast"
            >
              {tasks.data?.more ? '99+' : count}
            </span>
          )}
        </IconButton>
      }
      groups={[
        {
          id: 'mine',
          title: 'Für Sie',
          items: mine.slice(0, SHOWN).map((message) => ({
            id: message.id,
            label: notificationTitle[message.kind],
            description: [message.caseName, formatDate(message.createdAt)]
              .filter(Boolean)
              .join(' · '),
            icon: Inbox,
            onAction: () => {
              read.mutate(message.id);
              void navigate(
                message.caseId ? messageTarget(message.kind, message.caseId) : { to: '/' },
              );
            },
          })),
        },
        {
          id: 'tasks',
          title: 'Braucht die Kanzlei',
          items:
            open.length === 0
              ? [{ id: 'none', label: 'Gerade wartet nichts auf Sie.', isDisabled: true }]
              : open.slice(0, SHOWN).map((task) => ({
                  id: task.id,
                  label: task.title,
                  description:
                    task.kind === 'clarification'
                      ? `Zu klären · offen seit ${formatDate(task.createdAt)}`
                      : `Hinweis · am ${formatDate(task.createdAt)}`,
                  icon: task.kind === 'clarification' ? CircleHelp : Bell,
                  onAction: () => void navigate({ to: placeOf(task) }),
                })),
        },
        {
          id: 'all-tasks',
          // Section and entry need keys of their own: one collection holds both.
          items: [
            {
              id: 'inbox',
              label: unread > 0 ? `Alle Mitteilungen (${String(unread)} neu)` : 'Alle Mitteilungen',
              onAction: () => void navigate({ to: '/inbox' }),
            },
            {
              id: 'show-all',
              label: 'Alle Aufgaben auf der Startseite',
              onAction: () => void navigate({ to: '/' }),
            },
          ],
        },
      ]}
    />
  );
}
